import { styled } from '@mui/system';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import { Container, Item } from 'Components/Grid';
import InfoPopover from 'Components/InfoPopover';
import InputHandler from 'Components/InputHandler';
import { cerad } from 'neuro-schemas';
import { sortPartialDate } from 'neuro-utils';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { filterByDocumentType } from 'Utility/documentHandling';
import colors from '../../../../../config/theme/colors';
import MMSEDialog from './MMSEDialog';

import type { TRelevantDocumentType } from '../../../utils';
import type { ICERAD2023Computed } from 'neuro-schemas/cerad';

const {
  utils: {
    calculateCeradTotal,
    checkIfScoreIsAbnormal,
    getCeradAbnormalTasks,
    getCeradTotalCutOffScore,
    getCeradUnfilledTasks,
    getCeradUpdatedComputedData,
    getComputedDataThatShouldUpdate,
    getComputedValue,
  },
  config: { cutOffScoresByEducationYears, maxScores, taskToTitleMap },
} = cerad;

const StyledContainer = styled(Container)({
  padding: '0.5rem 0 0.5rem 0',
  color: colors.tertiaryText,
});

const columnSizes = [5, 3, 2, true];

const content = {
  verbalFluency: [],
  naming: [],
  mmse: [],
  wordListLearning: ['wordListTrial1', 'wordListTrial2', 'wordListTrial3', 'wordListLearningTotal'],
  copyFigure: ['copyFigure1', 'copyFigure2', 'copyFigure3', 'copyFigure4', 'copyFigureTotal'],
  wordListDelayedRecall: ['wordListDelayedRecallRaw', 'wordListDelayedRecallRetentionPercentage'],
  wordListRecognition: ['wordListRecognitionYes', 'wordListRecognitionNo', 'wordListRecognitionTotal'],
  delayedFigure: [
    'delayedFigure1',
    'delayedFigure2',
    'delayedFigure3',
    'delayedFigure4',
    'delayedFiguresTotal',
    'delayedFiguresRetentionPercentage',
  ],
  clockDrawing: [],
};

const HeaderRow = ({
  fm,
}: {
  fm: (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) => React.ReactNode[];
}): React.JSX.Element => {
  return (
    <StyledContainer style={{ padding: '1rem 0 1rem 0', borderBottom: `1px solid ${colors.darkGray}` }}>
      <Item xs={columnSizes[0]}>{fm('cognition.cerad.task')}</Item>
      <Item xs={columnSizes[1]} style={{ fontWeight: 600 }}>
        {fm('cognition.cerad.score')}
      </Item>
      <Item xs={columnSizes[2]} style={{ fontWeight: 600 }}>
        {fm('cognition.cerad.cutOffScore')}
      </Item>
      <Item xs={columnSizes[3]} style={{ fontWeight: 600 }}>
        {fm('cognition.cerad.result')}
      </Item>
    </StyledContainer>
  );
};

const TaskRow = ({
  documents,
  formData,
  viewing,
  task,
  index,
  computedData,
  setComputedData,
  fm,
}: Pick<IOwnProps, 'documents' | 'formData' | 'viewing'> & {
  task: keyof typeof content;
  index: number;
  computedData: ICERAD2023Computed;
  setComputedData: React.Dispatch<React.SetStateAction<ICERAD2023Computed>>;
  fm: (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) => React.ReactNode[];
}): React.JSX.Element => {
  const subContent = Array.isArray(content[task]) && content[task].length > 0 ? content[task] : undefined;
  const cutOffScores = computedData.educationYears && cutOffScoresByEducationYears[computedData.educationYears];

  const taskIsComputed = task === 'mmse' || task.includes('Total') || task.includes('Percentage');
  const taskCutOffScore = cutOffScores?.[task as keyof typeof cutOffScores];
  const taskMaxScore = maxScores[task as keyof typeof maxScores];

  const taskScore = taskIsComputed
    ? computedData[task as keyof typeof computedData]
    : formData.document[task as keyof typeof formData.document];

  const handleChange = (values: TOnChangeValues): void => {
    formData.onChange?.(values);

    const t = Object.keys(values)[0];
    if (!t) return;
    const newComputedData = getComputedDataThatShouldUpdate(t).reduce(
      (current, key) => {
        const computedValue = getComputedValue({ ...formData.document, ...values }, key);
        current[key] = computedValue;
        return current;
      },
      {} as { [key: string]: number | undefined },
    );

    setComputedData(prev => ({ ...prev, ...newComputedData }));
  };

  return (
    <React.Fragment key={`${task}-${index}-content`}>
      <StyledContainer
        style={{
          backgroundColor: index % 2 !== 0 ? colors.lightestGray : undefined,
          alignItems: task === 'mmse' ? 'center' : undefined,
        }}
      >
        <Item xs={columnSizes[0]} style={{ fontWeight: 600 }}>
          {`${index + 1}. ${fm(`cognition.cerad.${task}`)}`}
        </Item>
        <Item
          xs={task === 'mmse' ? true : columnSizes[1]}
          style={{ color: taskIsComputed ? colors.primaryText : undefined }}
        >
          {!subContent &&
            (task === 'mmse' ? (
              <MMSEDialog {...{ documents, formData, viewing, fm }} />
            ) : (
              <InputHandler
                type="NumberField"
                editing={!viewing && !taskIsComputed}
                name={task}
                formData={
                  taskIsComputed
                    ? { onChange: () => {}, document: { [task]: computedData[task as keyof typeof computedData] } }
                    : { onChange: handleChange, document: formData.document }
                }
                placeholder={!taskMaxScore ? '' : taskMaxScore === '-' ? '-' : `0-${taskMaxScore}`}
                width={18}
                min={0}
                max={typeof taskMaxScore === 'number' ? taskMaxScore : undefined}
              />
            ))}
        </Item>
        {task !== 'mmse' && (
          <React.Fragment>
            <Item xs={columnSizes[2]}>{typeof taskCutOffScore === 'number' ? `< ${taskCutOffScore}` : ''}</Item>
            {typeof taskScore === 'number' && checkIfScoreIsAbnormal(taskScore, taskCutOffScore) === 'abnormal' && (
              <Item xs={columnSizes[3]} style={{ color: colors.primaryText, fontWeight: 600 }}>
                {fm('cognition.cerad.abnormal')}
              </Item>
            )}
          </React.Fragment>
        )}
      </StyledContainer>
      {subContent &&
        subContent.map((subTask, subTaskIndex) => {
          const subTaskIsComputed = subTask.includes('Total') || subTask.includes('Percentage');
          const subTaskCutOffScore = cutOffScores?.[subTask as keyof typeof cutOffScores];
          const subTaskMaxScore = maxScores[subTask as keyof typeof maxScores];

          const subTaskScore = subTaskIsComputed
            ? computedData[subTask as keyof typeof computedData]
            : formData.document[subTask as keyof typeof formData.document];

          return (
            <StyledContainer
              key={`${subTask}-${subTaskIndex}-content`}
              style={{ backgroundColor: index % 2 !== 0 ? colors.lightestGray : undefined }}
            >
              <Item xs={columnSizes[0]} style={{ paddingLeft: '1.5rem' }}>
                <span>{'- '}</span>
                <span>{fm(`cognition.cerad.${subTask}`)}</span>
              </Item>
              <Item xs={columnSizes[1]} style={{ color: subTaskIsComputed ? colors.primaryText : undefined }}>
                <InputHandler
                  type="NumberField"
                  editing={!viewing && !subTaskIsComputed}
                  name={subTask}
                  formData={
                    subTaskIsComputed
                      ? {
                          onChange: () => {},
                          document: { [subTask]: computedData[subTask as keyof typeof computedData] },
                        }
                      : {
                          onChange: handleChange,
                          document: formData.document,
                        }
                  }
                  placeholder={!subTaskMaxScore ? '' : subTaskMaxScore === '-' ? '-' : `0-${subTaskMaxScore}`}
                  disablePlaceholderFormatting
                  width={18}
                  min={0}
                  max={typeof subTaskMaxScore === 'number' ? subTaskMaxScore : undefined}
                />
              </Item>
              <Item xs={columnSizes[2]}>{typeof subTaskCutOffScore === 'number' ? `< ${subTaskCutOffScore}` : ''}</Item>
              {typeof subTaskScore === 'number' &&
                checkIfScoreIsAbnormal(subTaskScore, subTaskCutOffScore) === 'abnormal' && (
                  <Item xs={columnSizes[3]} style={{ color: colors.primaryText, fontWeight: 600 }}>
                    {fm('cognition.cerad.abnormal')}
                  </Item>
                )}
            </StyledContainer>
          );
        })}
    </React.Fragment>
  );
};

const FooterRow = ({
  fm,
}: {
  fm: (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) => React.ReactNode[];
}): React.JSX.Element => {
  return (
    <div style={{ padding: '1rem 0 1rem 0', color: colors.tertiaryText, fontSize: '1.4rem', fontStyle: 'italic' }}>
      <div>{fm('cognition.cerad.wordListDelayedRecallRetentionPercentageFootnote')}</div>
      <div>{fm('cognition.cerad.delayedFiguresRetentionPercentageFootnote')}</div>
    </div>
  );
};

const CERADForm = ({ documents, formData, viewing }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element>) =>
    formatMessage(
      { id },
      values ?? {
        sup: (chunks: string[]) => <sup key={`${chunks}`}>{React.Children.toArray(chunks)}</sup>,
      },
    );

  const [computedData, setComputedData] = React.useState<ICERAD2023Computed>({});

  const backgroundDocsWithEducationYears = filterByDocumentType(documents, 'background').filter(
    d => typeof d.educationYears === 'number' && sortPartialDate(d.date, formData.document.date) <= 0,
  );
  const latestEducationYears = backgroundDocsWithEducationYears[0]?.educationYears;
  const { educationYears } = formData.document;

  React.useEffect(() => {
    setComputedData(prev => ({
      ...prev,
      ...getCeradUpdatedComputedData(formData.document, backgroundDocsWithEducationYears, true),
    }));
  }, [backgroundDocsWithEducationYears.length, educationYears]);

  const totalCutOffScore = getCeradTotalCutOffScore(educationYears ?? undefined);
  const totalScore =
    calculateCeradTotal(
      formData.document.verbalFluency ?? undefined,
      formData.document.naming ?? undefined,
      computedData.wordListLearningTotal,
      computedData.copyFigureTotal,
      formData.document.wordListDelayedRecallRaw ?? undefined,
      formData.document.wordListRecognitionYes ?? undefined,
      formData.document.wordListRecognitionNo ?? undefined,
      computedData.delayedFiguresTotal,
    ) ?? '-';

  const unfilledTasks = getCeradUnfilledTasks(formData.document, computedData);
  const abnormalTasks = getCeradAbnormalTasks(formData.document, computedData);

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="cognition.cerad.educationYears">
        <InputHandler
          type="Radio"
          editing={!viewing && (!latestEducationYears || !!educationYears)}
          name="educationYears"
          formData={
            !latestEducationYears
              ? formData
              : {
                  onChange: values => setComputedData(prev => ({ ...prev, ...values })),
                  document: { educationYears: computedData.educationYears },
                }
          }
          options={['≤10', '≥11']}
        />
      </FormRow>
      <FormSection>
        <div style={{ marginBottom: '4.5rem' }}>
          <HeaderRow fm={fm} />
          {(Object.keys(content) as (keyof typeof content)[]).map((c, i) => {
            return (
              <TaskRow
                key={`taskrow-${i}`}
                documents={documents}
                formData={formData}
                viewing={viewing}
                task={c}
                index={i}
                computedData={computedData}
                setComputedData={setComputedData}
                fm={fm}
              />
            );
          })}
        </div>
        <StyledContainer style={{ marginBottom: '4.5rem' }}>
          <Item xs={columnSizes[0]}>{fm('cognition.cerad.total')}</Item>
          <Item xs={columnSizes[1]} style={{ fontWeight: 600, color: colors.primaryText }}>
            {totalScore === '-' && unfilledTasks.length > 0 ? (
              <InfoPopover
                text="general.notFilledInformative"
                color="primary"
                values={[
                  ...new Set(
                    ['', ...unfilledTasks].map((task, index) => {
                      if (index === 0) return task;
                      const t = taskToTitleMap[task] ?? task;
                      return fm(`cognition.cerad.${t}`);
                    }),
                  ),
                ].join('\n')}
              />
            ) : (
              totalScore
            )}
            <span style={{ fontWeight: 400 }}>{` / ${maxScores.total}`}</span>
          </Item>
          <Item xs={columnSizes[2]}>{typeof totalCutOffScore === 'number' ? `< ${totalCutOffScore}` : ''}</Item>
          {typeof totalScore === 'number' && checkIfScoreIsAbnormal(totalScore, totalCutOffScore) === 'abnormal' && (
            <Item xs={columnSizes[3]} style={{ color: colors.primaryText, fontWeight: 600 }}>
              {fm('cognition.cerad.abnormal')}
            </Item>
          )}
        </StyledContainer>
        {abnormalTasks.length > 0 && (
          <FormRow title="cognition.cerad.abnormalTasks">
            {abnormalTasks.map(task => {
              const t = taskToTitleMap[task] ?? task;
              return <div key={t}>{fm(`cognition.cerad.${t}`)}</div>;
            })}
          </FormRow>
        )}
        <FormRow title="general.optional">
          <InputHandler
            type="TextArea"
            editing={!viewing}
            name="additionalInformation"
            formData={formData}
            placeholder="general.optional"
          />
        </FormRow>
        <FooterRow fm={fm} />
        <div style={{ fontStyle: 'italic', color: colors.secondaryText }}>
          Hallikainen I, Alenius M, Hokkanen L, Karrasch M, Krüger J, Ngandu T, Paajanen T, Rosenvall A, Suhonen N &
          Hänninen T. CERAD-tehtäväsarjaan koulutustason huomioivat katkaisurajat ja kokonaispistemäärä käyttöön. Suomen
          Lääkärilehti 2023; 78: e35809. Julkaistu 27. huhtikuuta 2023.
        </div>
      </FormSection>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
  formData: IFormData<IDocumentVersionV2<'cerad', '2023'>>;
  viewing?: boolean;
}

export default CERADForm;
